import { useRef } from 'react'

import { Box, FloraButton, Flex, FloraTypography, Toaster } from '@grupoboticario/flora-react'

import EventInformation from '@/components/EventInformation'
import EventRegulations from '@/components/EventRegulations'
import EventSiteDisplay from '@/components/EventSiteDisplay'
import { ACTION_TYPE } from '@/constants/actionTypes'
import { ERROR_MESSAGES } from '@/constants/eventMessages'
import { SECTION_TITLES, BUTTON_LABELS, SITE_DISPLAY_DESCRIPTION } from '@/constants/eventSectionTitles'
import { hasRedemptions, hasSelectedBrand } from '@/utils/eventValidations'

import { styles } from './EventForm.styles'
import EventPreviewModal from './EventPreviewModal'
import { useEventPreviewModal } from './useEventPreviewModal'

const EventForm = ({
  formValues,
  isNewEvent,
  isReadOnly = false,
  isLoading = false,
  setFieldValue,
  onSubmit,
  onCancel
}) => {
  const { isOpen: isPreviewModalOpen, openModal, closeModal, setIsOpen: setIsPreviewModalOpen } = useEventPreviewModal()
  const formRef = useRef(null)

  const getEventFormConfig = () => {
    const hasRedemptionsValue = hasRedemptions(formValues)
    const isRegulationsReadOnly = hasRedemptionsValue && !isNewEvent
    return {
      submitButtonLabel: isNewEvent ? BUTTON_LABELS.CREATE : BUTTON_LABELS.UPDATE,
      cancelButtonLabel: isReadOnly ? BUTTON_LABELS.BACK : BUTTON_LABELS.CANCEL,
      isRegulationsReadOnly,
      showSiteDisplayDescription: !isNewEvent,
      action: isNewEvent ? ACTION_TYPE.PREVIEW : ACTION_TYPE.SUBMIT,
      hasRedemptionsValue
    }
  }
  const config = getEventFormConfig()

  const handleFormSubmit = (e) => {
    e.preventDefault()

    if (!hasSelectedBrand(formValues.brands)) {
      Toaster.notify({
        kind: 'error',
        description: ERROR_MESSAGES.BRANDS_REQUIRED
      })
      return
    }

    if (config.action === ACTION_TYPE.PREVIEW) {
      openModal()
    } else {
      onSubmit(e)
    }
  }

  const handleConfirmCreate = () => {
    closeModal()

    onSubmit()
  }

  return (
    <Box css={styles.container}>
      <form onSubmit={handleFormSubmit} ref={formRef}>
        <Flex align="center" css={styles.sectionHeader} justify="space-between">
          <FloraTypography as="h5" fontSize="title">
            {SECTION_TITLES.EVENT_DETAILS}
          </FloraTypography>
        </Flex>

        <EventInformation isReadOnly={isReadOnly} setFieldValue={setFieldValue} values={formValues} />

        <Flex align="center" css={styles.sectionHeader} justify="space-between">
          <FloraTypography as="h5" fontSize="title">
            Regulamento e Perguntas frequentes
          </FloraTypography>
        </Flex>

        {config.isRegulationsReadOnly && (
          <Box css={styles.warningBox}>
            <FloraTypography fontSize="bodyLargeStandard" css={styles.warningText}>
              ⚠️ Este evento já possui ingressos resgatados. Por questões de integridade e conformidade, não é possível
              editar o regulamento/FAQ após o início dos resgates.
            </FloraTypography>
          </Box>
        )}

        <EventRegulations
          isReadOnly={isReadOnly || config.isRegulationsReadOnly}
          setFieldValue={setFieldValue}
          values={formValues}
        />

        <Flex align="flex-start" css={styles.siteDisplayHeader} direction="column" justify="space-between">
          <FloraTypography as="h5" fontSize="title">
            {SECTION_TITLES.SITE_DISPLAY}
          </FloraTypography>
          {config.showSiteDisplayDescription && (
            <FloraTypography fontSize="bodyLargeStandard" css={styles.descriptionText}>
              {SITE_DISPLAY_DESCRIPTION}
            </FloraTypography>
          )}
        </Flex>

        <EventSiteDisplay
          isNewEvent={isNewEvent}
          isReadOnly={isReadOnly}
          setFieldValue={setFieldValue}
          values={formValues}
        />

        <Flex css={styles.buttonsContainer} gap="$4" justify="flex-end">
          <FloraButton onClick={onCancel} type="button" hierarchy="secondary">
            {config.cancelButtonLabel}
          </FloraButton>
          {!isReadOnly && (
            <FloraButton disabled={isLoading} isLoading={isLoading} type="submit">
              {config.submitButtonLabel}
            </FloraButton>
          )}
        </Flex>
      </form>

      <EventPreviewModal
        formValues={formValues}
        isLoading={isLoading}
        onConfirm={handleConfirmCreate}
        onOpenChange={setIsPreviewModalOpen}
        open={isPreviewModalOpen}
      />
    </Box>
  )
}

export default EventForm
